import { LITE_PLAN_ID, WEEKLY_RECURRENCE } from 'consts/user'
import { TUserSubscriptions, IUnlimitedAiVideoModel } from 'types/user'

const getHighestSubscriptionFromFiltered = (
  filteredSubscriptions: TUserSubscriptions
) => {
  if (filteredSubscriptions.length === 0) return null

  const _highestSubscription = filteredSubscriptions[0]

  // Prioritize monthly lite subscription over weekly when both exist (backend considers weekly as higher due to hdMax)
  const isDuplicatedLiteSubscriptions =
    filteredSubscriptions.length > 1 &&
    _highestSubscription.tariffId === LITE_PLAN_ID &&
    _highestSubscription.recurrencePeriod === WEEKLY_RECURRENCE

  const highestSubscription = isDuplicatedLiteSubscriptions
    ? filteredSubscriptions[1]
    : filteredSubscriptions[0]
  const { tariffId, title, recurrencePeriod } = highestSubscription
  return {
    tariffId,
    title,
    recurrencePeriod
  }
}

export const getActiveHighestSubscriptionData = (
  subscriptions: TUserSubscriptions
) => {
  if (subscriptions.length === 0) return null

  const activeRecurringSubscriptions = subscriptions.filter(
    subscription => subscription.recurring === 'active'
  )

  if (activeRecurringSubscriptions.length === 0) return null

  return getHighestSubscriptionFromFiltered(activeRecurringSubscriptions)
}

export const getUserCurrentHighestSubscription = (
  subscriptions: TUserSubscriptions
) => getHighestSubscriptionFromFiltered(subscriptions)

export const collectUniqueUnlimitedAiImageModelIds = (
  subscriptions: TUserSubscriptions
): number[] => {
  const ids = subscriptions.flatMap(
    subscription =>
      subscription.unlimitedAiImageModels?.map(model => model.aiImageModelId) ||
      []
  )

  return [...new Set(ids)]
}

/**
 * Collects unlimited AI video models from all subscriptions,
 * merging quality values across subscriptions for the same model ID.
 * E.g. if sub1 has model 6 with [480, 720] and sub2 has model 6 with [720, 1080],
 * the result will be model 6 with [480, 720, 1080].
 */
export const collectUniqueUnlimitedAiVideoModels = (
  subscriptions: TUserSubscriptions
): IUnlimitedAiVideoModel[] => {
  const modelMap = new Map<number, Set<number>>()

  subscriptions.forEach(subscription => {
    subscription.unlimitedAiVideoModels?.forEach(model => {
      if (!modelMap.has(model.aiVideoModelId)) {
        modelMap.set(model.aiVideoModelId, new Set())
      }
      model.qualities.forEach(q =>
        modelMap.get(model.aiVideoModelId)!.add(q.value)
      )
    })
  })

  return Array.from(modelMap, ([aiVideoModelId, qualities]) => ({
    aiVideoModelId,
    qualities: Array.from(qualities, value => ({ value }))
  }))
}
